import React, { useState } from 'react';
import { Card } from '@repo/ui/Card';
import { useNavigate } from 'react-router';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Button } from '@repo/ui/Button';
import { useZodForm } from '@repo/hooks/useZodForm';

import { DeleteDialog } from '@repo/ui/Dialog';
import { Form } from '@repo/ui/Form';
import type { Mercury } from '@repo/types/Models';
import { DeleteBlockedNotice } from '@/components/DeleteBlockedNotice';

export interface DeleteApiKeyProps {
  name: Mercury.Models.ApiKey['name'];
  loading: boolean;
  apiKeyId: Mercury.Models.ApiKey['id'];
  accountId: Mercury.Models.Account['id'];
  navigateTo?: string;
  /** Numbers are still attached, so deleting is blocked. */
  hasDids?: boolean;
  /** Still checking for attached numbers; delete stays disabled meanwhile. */
  checkingDids?: boolean;
  /** The key's Numbers tab, linked from the blocked notice. */
  numbersHref?: string;
}

const DeleteApiKey = ({
  name,
  apiKeyId,
  loading,
  accountId,
  navigateTo,
  hasDids = false,
  checkingDids = false,
  numbersHref,
}: DeleteApiKeyProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const DELETE_API_KEY = gql`
    mutation DeleteApiKey($id: ID!) {
      deleteApiKey(id: $id) {
        apiKey {
          id
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async () => {
    setIsOpen(false);
    await client.mutate({
      mutation: DELETE_API_KEY,
      variables: {
        id: apiKeyId,
      },
    });
    setTimeout(() => {
      navigate(navigateTo ?? `/a/${accountId}/develop`);
    }, 2000);
  };

  const form = useZodForm({
    onSubmit,
  });

  return (
    <>
      <Card title="Delete Api Key">
        <div className="flex flex-col gap-4 items-start">
          {hasDids && numbersHref && (
            <DeleteBlockedNotice noun="API key" numbersHref={numbersHref} />
          )}
          <Button
            loading={loading}
            variant="danger"
            disabled={hasDids || checkingDids}
            onClick={() => setIsOpen(true)}
          >
            Delete API Key
          </Button>
        </div>
      </Card>

      <DeleteDialog
        title={`Are you sure you want to delete this API Key?`}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        centered
        form={form}
        submitText="Yes, delete API Key"
        cancelText="No, abort"
      >
        <Form.Success>API Key successfully deleted</Form.Success>
        <Form.Error>
          Unable to delete API Key. Please try again later or contact support if error persists.
        </Form.Error>
        <div className=" col-span-2 flex flex-col w-full gap-2 py-3 border border-l-0 border-r-0 mb-7 border-grey-400/50">
          <span className="text-2xl font-bold">{name}</span>
          <span>ID: {apiKeyId}</span>
        </div>
      </DeleteDialog>
    </>
  );
};

export default DeleteApiKey;
